import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import FloatingDock from '@/components/layout/FloatingDock';
import {
  FileText, Heart, Users, Shield, Trash2, Archive, RefreshCw,
  ChevronRight, Edit2, X, Check, AlertTriangle, Info,
  Lock, Sparkles, Save, User, Mail, MessageSquare
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Will option icons
const OPTION_ICONS = {
  memorialize: Heart,
  delete: Trash2,
  transfer: RefreshCw,
  archive: Archive
};

const OPTION_COLORS = {
  memorialize: 'from-purple-500 to-pink-500',
  delete: 'from-red-500 to-orange-500',
  transfer: 'from-blue-500 to-cyan-500',
  archive: 'from-amber-500 to-yellow-500'
};

const DigitalWill = () => {
  const [user, setUser] = useState(null);
  
  useEffect(() => {
    const userData = localStorage.getItem('socyy_user');
    if (userData) setUser(JSON.parse(userData));
  }, []);
  const [willOptions, setWillOptions] = useState([]);
  const [currentWill, setCurrentWill] = useState(null);
  const [hasWill, setHasWill] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    will_option: '',
    primary_beneficiary_email: '',
    primary_beneficiary_name: '',
    primary_beneficiary_relationship: '',
    backup_beneficiary_email: '',
    backup_beneficiary_name: '',
    backup_beneficiary_relationship: '',
    special_instructions: '',
    transfer_recipient_email: '',
    farewell_message: '',
    notify_followers: true
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [optionsRes, willRes] = await Promise.all([
        axios.get(`${API_URL}/api/digital-will/options`),
        axios.get(`${API_URL}/api/digital-will/`, { withCredentials: true })
      ]);
      
      setWillOptions(optionsRes.data.options || []);
      setHasWill(willRes.data.has_will);
      
      if (willRes.data.has_will && willRes.data.will) {
        setCurrentWill(willRes.data.will);
        // Pre-fill form with existing data
        const will = willRes.data.will;
        setFormData({
          will_option: will.will_option || '',
          primary_beneficiary_email: will.primary_beneficiary?.email || '',
          primary_beneficiary_name: will.primary_beneficiary?.name || '',
          primary_beneficiary_relationship: will.primary_beneficiary?.relationship || '',
          backup_beneficiary_email: will.backup_beneficiary?.email || '',
          backup_beneficiary_name: will.backup_beneficiary?.name || '',
          backup_beneficiary_relationship: will.backup_beneficiary?.relationship || '',
          special_instructions: will.special_instructions || '',
          transfer_recipient_email: will.transfer_recipient?.email || '',
          farewell_message: will.farewell_message || '',
          notify_followers: will.notify_followers ?? true
        });
        setSelectedOption(will.will_option);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const selectOption = (optionId) => {
    setSelectedOption(optionId);
    setFormData({ ...formData, will_option: optionId });
    setShowEditor(true);
  };

  const saveWill = async () => {
    if (!formData.will_option) {
      toast.error('Please select what should happen to your account');
      return;
    }
    if (!formData.primary_beneficiary_email || !formData.primary_beneficiary_name) {
      toast.error('Please add a primary beneficiary');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/digital-will/`, formData, { withCredentials: true });
      toast.success('Digital Will saved! 📜');
      setShowEditor(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save will');
    } finally {
      setSaving(false);
    }
  };

  const cancelWill = async () => {
    const confirmed = window.confirm(
      '⚠️ CANCEL YOUR DIGITAL WILL?\n\n' +
      'This will remove all your legacy preferences.\n' +
      'Your account will follow default policies if something happens.\n\n' +
      'Are you sure?'
    );
    if (!confirmed) return;
    
    try {
      await axios.delete(`${API_URL}/api/digital-will/`, { withCredentials: true });
      toast.success('Digital Will cancelled');
      setCurrentWill(null);
      setHasWill(false);
      setFormData({
        will_option: '',
        primary_beneficiary_email: '',
        primary_beneficiary_name: '',
        primary_beneficiary_relationship: '',
        backup_beneficiary_email: '',
        backup_beneficiary_name: '',
        backup_beneficiary_relationship: '',
        special_instructions: '',
        transfer_recipient_email: '',
        farewell_message: '',
        notify_followers: true
      });
      setSelectedOption(null);
    } catch (error) {
      toast.error('Failed to cancel will');
    }
  };

  const getOptionDetails = (optionId) => {
    return willOptions.find(o => o.id === optionId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-slate-500 to-blue-500 flex items-center justify-center mx-auto mb-4 animate-pulse">
            <FileText className="w-8 h-8 text-white" />
          </div>
          <p className="text-gray-600 dark:text-gray-400">Loading your Digital Will...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pb-24">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-slate-600 via-blue-600 to-indigo-600 flex items-center justify-center shadow-lg">
              <FileText className="w-8 h-8 text-white" />
            </div>
            <div className="text-left">
              <h1 className="text-4xl font-heading font-black text-gray-900 dark:text-white">
                Digital Will
              </h1>
              <p className="text-gray-600 dark:text-gray-400">Your Digital Legacy Plan</p>
            </div>
          </div>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Decide what happens to your Socyy account, posts, messages, and memories.
            Your wishes will be honored when the time comes.
          </p>
        </motion.div>

        {/* Current Will Status */}
        {hasWill && currentWill && !showEditor && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="rounded-3xl overflow-hidden bg-white/70 dark:bg-gray-800/70 border border-white/20 shadow-xl mb-8">
              <div className={`bg-gradient-to-r ${OPTION_COLORS[currentWill.will_option]} p-6 text-white`}>
                <div className="flex items-center gap-4">
                  {(() => {
                    const Icon = OPTION_ICONS[currentWill.will_option] || FileText;
                    return (
                      <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                        <Icon className="w-7 h-7" />
                      </div>
                    );
                  })()}
                  <div>
                    <Badge className="bg-white/20 text-white mb-2">Active Will</Badge>
                    <h2 className="text-2xl font-bold">
                      {getOptionDetails(currentWill.will_option)?.label || 'Digital Will'}
                    </h2>
                    <p className="text-white/80">
                      {getOptionDetails(currentWill.will_option)?.description}
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6">
                {/* Primary Beneficiary */}
                <div className="mb-6">
                  <h3 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <Heart className="w-5 h-5 text-pink-500" />
                    Primary Beneficiary
                  </h3>
                  <div className="bg-pink-50 dark:bg-pink-900/20 rounded-xl p-4">
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {currentWill.primary_beneficiary?.name}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {currentWill.primary_beneficiary?.email} • {currentWill.primary_beneficiary?.relationship}
                    </p>
                  </div>
                </div>

                {/* Backup Beneficiary */}
                {currentWill.backup_beneficiary?.email && (
                  <div className="mb-6">
                    <h3 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                      <Users className="w-5 h-5 text-blue-500" />
                      Backup Beneficiary
                    </h3>
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4">
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {currentWill.backup_beneficiary?.name}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {currentWill.backup_beneficiary?.email} • {currentWill.backup_beneficiary?.relationship}
                      </p>
                    </div>
                  </div>
                )}

                {/* Farewell Message */}
                {currentWill.farewell_message && (
                  <div className="mb-6">
                    <h3 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                      <MessageSquare className="w-5 h-5 text-purple-500" />
                      Farewell Message
                    </h3>
                    <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4">
                      <p className="text-gray-700 dark:text-gray-300 italic">
                        "{currentWill.farewell_message}"
                      </p>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3">
                  <Button
                    onClick={() => setShowEditor(true)}
                    className="flex-1 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500"
                    data-testid="edit-will-btn"
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit Will
                  </Button>
                  <Button
                    onClick={cancelWill}
                    variant="outline"
                    className="rounded-full border-red-300 text-red-600 hover:bg-red-50"
                    data-testid="cancel-will-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Cancel Will
                  </Button>
                </div>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Will Options Selection */}
        {(!hasWill || showEditor) && (
          <>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
              {hasWill ? 'Update Your Digital Will' : 'What should happen to your account?'}
            </h2>

            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {willOptions.map((option) => {
                const Icon = OPTION_ICONS[option.id] || FileText;
                const isSelected = selectedOption === option.id;
                
                return (
                  <motion.div
                    key={option.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => selectOption(option.id)}
                    className={`cursor-pointer rounded-2xl p-6 border-2 transition-all ${
                      isSelected
                        ? `border-transparent bg-gradient-to-br ${OPTION_COLORS[option.id]} text-white shadow-xl`
                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300'
                    }`}
                    data-testid={`option-${option.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        isSelected ? 'bg-white/20' : 'bg-gray-100 dark:bg-gray-700'
                      }`}>
                        <span className="text-2xl">{option.icon}</span>
                      </div>
                      <div className="flex-1">
                        <h3 className={`font-bold text-lg mb-1 ${
                          isSelected ? 'text-white' : 'text-gray-900 dark:text-white'
                        }`}>
                          {option.label}
                        </h3>
                        <p className={`text-sm ${
                          isSelected ? 'text-white/80' : 'text-gray-600 dark:text-gray-400'
                        }`}>
                          {option.description}
                        </p>
                      </div>
                      {isSelected && (
                        <Check className="w-6 h-6" />
                      )}
                    </div>
                    
                    {/* Details on selection */}
                    {isSelected && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="mt-4 pt-4 border-t border-white/20"
                      >
                        <ul className="space-y-1 text-sm text-white/90">
                          {option.details?.map((detail, i) => (
                            <li key={i} className="flex items-center gap-2">
                              <Check className="w-3 h-3" />
                              {detail}
                            </li>
                          ))}
                        </ul>
                      </motion.div>
                    )}
                  </motion.div>
                );
              })}
            </div>

            {/* Beneficiary Form */}
            {selectedOption && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card className="rounded-3xl p-6 bg-white/70 dark:bg-gray-800/70 border border-white/20 shadow-xl">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                    <Users className="w-6 h-6 text-pink-500" />
                    Beneficiary Details
                  </h3>

                  <div className="space-y-6">
                    {/* Primary Beneficiary */}
                    <div className="p-4 bg-pink-50 dark:bg-pink-900/20 rounded-xl">
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                        <Heart className="w-4 h-4 text-pink-500" />
                        Primary Beneficiary *
                      </h4>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div>
                          <Label>Full Name</Label>
                          <Input
                            value={formData.primary_beneficiary_name}
                            onChange={(e) => setFormData({ ...formData, primary_beneficiary_name: e.target.value })}
                            placeholder="John Doe"
                            className="rounded-xl mt-1"
                            data-testid="primary-name-input"
                          />
                        </div>
                        <div>
                          <Label>Email</Label>
                          <Input
                            type="email"
                            value={formData.primary_beneficiary_email}
                            onChange={(e) => setFormData({ ...formData, primary_beneficiary_email: e.target.value })}
                            placeholder="john@example.com"
                            className="rounded-xl mt-1"
                            data-testid="primary-email-input"
                          />
                        </div>
                        <div>
                          <Label>Relationship</Label>
                          <select
                            value={formData.primary_beneficiary_relationship}
                            onChange={(e) => setFormData({ ...formData, primary_beneficiary_relationship: e.target.value })}
                            className="w-full rounded-xl border border-gray-300 dark:border-gray-600 p-2.5 mt-1 bg-white dark:bg-gray-700"
                          >
                            <option value="">Select...</option>
                            <option value="spouse">Spouse/Partner</option>
                            <option value="child">Child</option>
                            <option value="parent">Parent</option>
                            <option value="sibling">Sibling</option>
                            <option value="friend">Friend</option>
                            <option value="attorney">Attorney/Legal Rep</option>
                            <option value="other">Other</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Backup Beneficiary */}
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                        <Users className="w-4 h-4 text-blue-500" />
                        Backup Beneficiary (Optional)
                      </h4>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div>
                          <Label>Full Name</Label>
                          <Input
                            value={formData.backup_beneficiary_name}
                            onChange={(e) => setFormData({ ...formData, backup_beneficiary_name: e.target.value })}
                            placeholder="Jane Doe"
                            className="rounded-xl mt-1"
                          />
                        </div>
                        <div>
                          <Label>Email</Label>
                          <Input
                            type="email"
                            value={formData.backup_beneficiary_email}
                            onChange={(e) => setFormData({ ...formData, backup_beneficiary_email: e.target.value })}
                            placeholder="jane@example.com"
                            className="rounded-xl mt-1"
                          />
                        </div>
                        <div>
                          <Label>Relationship</Label>
                          <select
                            value={formData.backup_beneficiary_relationship}
                            onChange={(e) => setFormData({ ...formData, backup_beneficiary_relationship: e.target.value })}
                            className="w-full rounded-xl border border-gray-300 dark:border-gray-600 p-2.5 mt-1 bg-white dark:bg-gray-700"
                          >
                            <option value="">Select...</option>
                            <option value="spouse">Spouse/Partner</option>
                            <option value="child">Child</option>
                            <option value="parent">Parent</option>
                            <option value="sibling">Sibling</option>
                            <option value="friend">Friend</option>
                            <option value="attorney">Attorney/Legal Rep</option>
                            <option value="other">Other</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Transfer Recipient (if transfer option selected) */}
                    {selectedOption === 'transfer' && (
                      <div className="p-4 bg-cyan-50 dark:bg-cyan-900/20 rounded-xl">
                        <h4 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                          <RefreshCw className="w-4 h-4 text-cyan-500" />
                          Account Transfer Recipient
                        </h4>
                        <div>
                          <Label>Email of person to receive account</Label>
                          <Input
                            type="email"
                            value={formData.transfer_recipient_email}
                            onChange={(e) => setFormData({ ...formData, transfer_recipient_email: e.target.value })}
                            placeholder="recipient@example.com"
                            className="rounded-xl mt-1"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            This person will gain full control of your Socyy account
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Farewell Message */}
                    <div>
                      <Label className="flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-purple-500" />
                        Farewell Message (Optional)
                      </Label>
                      <Textarea
                        value={formData.farewell_message}
                        onChange={(e) => setFormData({ ...formData, farewell_message: e.target.value })}
                        placeholder="A final message to share with your followers and friends..."
                        className="rounded-xl mt-2"
                        rows={4}
                        data-testid="farewell-message-input"
                      />
                    </div>

                    {/* Special Instructions */}
                    <div>
                      <Label className="flex items-center gap-2">
                        <Info className="w-4 h-4 text-amber-500" />
                        Special Instructions (Optional)
                      </Label>
                      <Textarea
                        value={formData.special_instructions}
                        onChange={(e) => setFormData({ ...formData, special_instructions: e.target.value })}
                        placeholder="Any special requests or instructions..."
                        className="rounded-xl mt-2"
                        rows={3}
                      />
                    </div>

                    {/* Notify Followers */}
                    <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                      <input
                        type="checkbox"
                        id="notify_followers"
                        checked={formData.notify_followers}
                        onChange={(e) => setFormData({ ...formData, notify_followers: e.target.checked })}
                        className="w-5 h-5 rounded"
                      />
                      <div>
                        <Label htmlFor="notify_followers" className="mb-0 cursor-pointer">
                          Notify my followers
                        </Label>
                        <p className="text-xs text-gray-500">
                          Your followers will be notified when your will is executed
                        </p>
                      </div>
                    </div>

                    {/* Save Button */}
                    <div className="flex gap-3 pt-4">
                      {hasWill && (
                        <Button
                          onClick={() => {
                            setShowEditor(false);
                            // Reset to original values
                            if (currentWill) {
                              setSelectedOption(currentWill.will_option);
                            }
                          }}
                          variant="outline"
                          className="flex-1 rounded-full"
                        >
                          Cancel
                        </Button>
                      )}
                      <Button
                        onClick={saveWill}
                        disabled={saving}
                        className={`flex-1 rounded-full bg-gradient-to-r ${OPTION_COLORS[selectedOption] || 'from-blue-500 to-indigo-500'} text-white font-bold h-12`}
                        data-testid="save-will-btn"
                      >
                        {saving ? 'Saving...' : (
                          <>
                            <Save className="w-4 h-4 mr-2" />
                            {hasWill ? 'Update My Will' : 'Save My Digital Will'}
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </Card>
              </motion.div>
            )}

            {/* Info Box */}
            <Card className="rounded-2xl p-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 mt-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-amber-800 dark:text-amber-200">Important Information</h4>
                  <ul className="text-sm text-amber-700 dark:text-amber-300 mt-2 space-y-1">
                    <li>• Your will can be changed at any time while your account is active</li>
                    <li>• Execution requires legal documentation (death certificate) and admin verification</li>
                    <li>• Your beneficiary will be contacted to confirm the request</li>
                    <li>• All data exports are encrypted and securely transmitted</li>
                    <li>• This feature complies with GDPR and data protection regulations</li>
                  </ul>
                </div>
              </div>
            </Card>
          </>
        )}
      </div>
      <FloatingDock user={user} />
    </div>
  );
};

export default DigitalWill;
